import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { ScheduledPostTarget } from 'types'
import { ProviderToName as BaseProviderToName } from 'components/ArticlePostPreview/PostPreviewBody/IntegrationsDrawer/constants'
import { hasPostSettings } from 'screens/Calendar/FullScreenPreview/utils'

const ProviderToName = {
  ...BaseProviderToName,
  [ExternalIntegrationEnum.X]: 'X / Twitter',
}

interface ValidationResult {
  isValid: boolean
  errors: string[]
}

export type ActionType =
  | 'connect'
  | 'post-now'
  | 'view-post'
  | 'retry'
  | 'retry-auth'
  | 'toggle'
  | 'loading'
  | 'posting'
  | 'locked'

interface ActionConfig {
  type: ActionType
  buttonTooltip?: string
  componentTooltip?: string
  disabled?: boolean
  onPress?: () => void
  showPostSettings?: boolean
}

interface GetTargetActionConfigParams {
  platform: ExternalIntegrationEnum
  target?: ScheduledPostTarget
  isAvailable: boolean
  validation: ValidationResult
  isLoading?: boolean
  isAlreadyPosted?: boolean
  isDisabled?: boolean
  hasTarget: boolean
  needsReauth?: boolean
  // Callback functions
  onConnect: () => void
  onToggle: (platform: ExternalIntegrationEnum, isChecked: boolean) => void
  onPublish: (targetId: number) => void
  onPublishWithoutTarget: () => void
  onViewPost: (url: string) => void
  onRetry: (targetId: number) => void
  onReauth: (platform: ExternalIntegrationEnum) => void
  isLocked?: boolean
}

export const getTargetActionConfig = ({
  platform,
  target,
  isAvailable,
  validation,
  isLoading = false,
  isAlreadyPosted = false,
  isDisabled = false,
  hasTarget,
  needsReauth = false,
  onConnect,
  onToggle,
  onPublish,
  onPublishWithoutTarget,
  onViewPost,
  onRetry,
  onReauth,
  isLocked = false,
}: GetTargetActionConfigParams): ActionConfig => {
  const platformName = ProviderToName[platform]
  const isProcessing = target?.status === 'queued' || target?.status === 'processing' || target?.status === 'retrying'
  const isFailure = target?.status === 'failed'
  const isPublished = target?.status === 'published'
  const showPostSettings = hasPostSettings(platform)

  // Loading state
  if (isLoading) {
    return { type: 'loading' }
  }

  // Processing state
  if (isProcessing) {
    return { type: 'posting' }
  }

  // Crossposts locked
  if (isLocked) {
    return {
      type: 'locked',
      buttonTooltip: 'Cross-posting to additional platforms is locked. Upgrade to unlock this feature.',
    }
  }

  // Not available - needs connection
  if (!isAvailable) {
    return {
      type: 'connect',
      buttonTooltip: 'Connect your account to post on this platform',
      onPress: onConnect,
    }
  }

  // PRIORITY: If target is successfully published with post_url, show View Post button
  // This should be checked early regardless of isAlreadyPosted status
  if (isPublished && target?.post_url) {
    return {
      type: 'view-post',
      onPress: () => onViewPost(target.post_url as string),
      buttonTooltip: `View post on ${platformName}`,
    }
  }

  // PRIORITY: Check auth before showing retry for failed targets
  // User must fix auth first before they can retry posting
  if (needsReauth) {
    return {
      type: 'retry-auth',
      componentTooltip: `Your ${platformName} connection expired - reconnect to enable posting`,
      onPress: () => onReauth(platform),
    }
  }

  // Target is failed - Show Retry button (only after auth is confirmed working)
  if (isFailure) {
    return {
      type: 'retry',
      componentTooltip: target?.error_message || '',
      buttonTooltip: 'Retry',
      onPress: () => onRetry(target?.id),
      showPostSettings,
    }
  }

  // State 1a: Already posted AND valid AND has target - Show enabled Post Now button
  if (isAlreadyPosted && validation.isValid && !isPublished && hasTarget) {
    return {
      type: 'post-now',
      onPress: () => onPublish((target as ScheduledPostTarget).id),
      showPostSettings,
    }
  }

  // State 1a2: Already posted AND valid AND no target yet - Show enabled Post Now button that will create target
  if (isAlreadyPosted && validation.isValid && !hasTarget) {
    return {
      type: 'post-now',
      onPress: onPublishWithoutTarget,
      buttonTooltip: `Publish to ${platformName}`,
      showPostSettings,
    }
  }

  // State 1b: Already posted AND invalid - Show disabled Post Now button with validation tooltip
  if (isAlreadyPosted && !validation.isValid) {
    return {
      type: 'post-now',
      componentTooltip: validation.errors.join('\n'),
      disabled: true,
      showPostSettings,
    }
  }

  // State 4: Not already posted but not valid - Show disabled toggle
  if (!isAlreadyPosted && !validation.isValid) {
    return {
      type: 'toggle',
      componentTooltip: validation.errors.join('\n'),
      disabled: true,
      onPress: () => onToggle(platform, !hasTarget),
      showPostSettings,
    }
  }

  // State 5: Default case - Show enabled toggle
  const toggleDisabled = !isAvailable || isDisabled || isAlreadyPosted
  const buttonTooltip =
    isDisabled && hasTarget ? 'You cannot disable the last platform while the post is scheduled' : ''

  return {
    type: 'toggle',
    buttonTooltip,
    disabled: toggleDisabled,
    onPress: () => onToggle(platform, !hasTarget),
    showPostSettings,
  }
}
